import { FileConversionService, Select, UploadFile, BankFileConversionRail, Icon, FieldData, FormCtx } from 'vopay';
import { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useFileConfigurations } from '../FileConfigurationProvider';
import useFileConversion from '@/hooks/useApi/useFileConversion';

export type ConvertMyTemplateFormValues = {
  Service: FileConversionService | '';
  FileBase64: string;
  FileName: string;
  PaymentRail?: BankFileConversionRail | '';
  ValueDate?: string;
  FileConversionConfigurationID?: string;
  FileConversionPreprocessingID?: string;
};

export const useConvertMyTemplateFormFields = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.FileUploadDrawer.ConvertTemplate' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { configurations, configurationsLoading } = useFileConfigurations();
  const { getPreprocessingConfigs, preprocessingConfigsLoading } = useFileConversion();
  const [preprocessingTemplates, setPreprocessingTemplates] = useState<Array<{ value: string; label: string }>>([]);

  const hasOnlyDefaultProcessor = useMemo(() => {
    return configurations.length === 1 && configurations[0].Processor === 'default_processor';
  }, [configurations]);

  const serviceOptions = useMemo(() => {
    let hasCredit = false;
    let hasDebit = false;

    if (configurations) {
      Object.values(configurations).forEach((config) => {
        if (config.PaymentRails?.credit && Object.keys(config.PaymentRails.credit).length > 0) {
          hasCredit = true;
        }
        if (config.PaymentRails?.debit && Object.keys(config.PaymentRails.debit).length > 0) {
          hasDebit = true;
        }
      });
    }

    if (!hasCredit && !hasDebit) {
      hasCredit = true;
      hasDebit = true;
    }

    const options = [];
    if (hasDebit) {
      options.push({ value: 'debit', label: tGlobal('ServiceOptions.debit') });
    }
    if (hasCredit) {
      options.push({ value: 'credit', label: tGlobal('ServiceOptions.credit') });
    }

    return options;
  }, [configurations]);

  const processConfig = async (fileBase64: string, ctx: FormCtx<ConvertMyTemplateFormValues>) => {
    if (!fileBase64) {
      setPreprocessingTemplates([]);
      return;
    }
    try {
      const response = await getPreprocessingConfigs({
        SourceFile: fileBase64,
      });

      if (response.Success && response.Configs && response.Configs.length > 0) {
        const templates = response.Configs.map((config) => ({
          value: config.FileConversionPreprocessingID,
          label: config.ConfigName,
        }));
        setPreprocessingTemplates(templates);

        // Set default template (first one or the one marked as default)
        const defaultTemplate = response.Configs.find((config) => config.IsDefault);
        if (defaultTemplate) {
          ctx.setFieldValue('FileConversionPreprocessingID', defaultTemplate.FileConversionPreprocessingID);
        } else if (templates.length > 0) {
          ctx.setFieldValue('FileConversionPreprocessingID', templates[0].value);
        }
      } else {
        setPreprocessingTemplates([]);
        ctx.setFieldValue('FileConversionPreprocessingID', '');
      }
    } catch (error) {
      console.error('Failed to fetch preprocessing templates:', error);
      setPreprocessingTemplates([]);
      ctx.setFieldValue('FileConversionPreprocessingID', '');
    }
  };

  const uploadFile: FieldData<ConvertMyTemplateFormValues>[] = [
    {
      fieldName: 'Service',
      label: () => t('serviceLabel', { defaultValue: 'Transfer Type' }),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData, ctx }) => {
        const data = getFormikData<ConvertMyTemplateFormValues['Service']>({});

        // Set the value automatically if there's only one option
        useEffect(() => {
          if (serviceOptions.length === 1 && !ctx.values.Service) {
            ctx.setFieldValue('Service', serviceOptions[0].value);
          }
        }, [serviceOptions, ctx.values.Service]);

        return (
          <Select
            version="2"
            {...data}
            options={serviceOptions}
            placeholder=""
            sort={false}
            disabled={data.disabled || serviceOptions.length === 1}
            loading={configurationsLoading}
          />
        );
      },
    },
    {
      fieldName: 'FileBase64',
      label: () => t('fileLabel', { defaultValue: 'Attachment' }),
      getInputState: () => 'required',
      showField: () => true,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData, ctx }) => {
        const data = getFormikData<ConvertMyTemplateFormValues['FileBase64']>({
          onChange(event) {
            const value = event.target.value;
            ctx.setFieldValue('FileBase64', value);
            processConfig(value, ctx);
          },
        });

        return (
          <div
            style={{
              position: 'relative',
            }}
          >
            <UploadFile
              id="FileBase64"
              {...data}
              variant="onboarding"
              onBlurFileName={(e: React.FocusEvent<HTMLInputElement>) => ctx.handleBlur(e)}
              onChangeFileName={(e: React.ChangeEvent<HTMLInputElement>) => {
                ctx.setFieldValue('FileName', e.target.value);
              }}
              idFileName="FileName"
              valueFileName={ctx.values.FileName}
              maxMBSize={20}
              keepExtension={true}
            />

            {preprocessingConfigsLoading && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: '#ffffffaa',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <Icon icon="loader" size="xxlg" />
              </div>
            )}
          </div>
        );
      },
    },
    {
      fieldName: 'FileConversionPreprocessingID',
      label: () => t('templateLabel', { defaultValue: 'Select Template' }),
      getInputState: () => (preprocessingTemplates.length > 1 ? 'required' : 'disabled'),
      showField: () => preprocessingTemplates.length > 1,
      validate: () => Yup.string().required(),
      renderField: ({ getFormikData }) => {
        const data = getFormikData<ConvertMyTemplateFormValues['FileConversionPreprocessingID']>({});
        return (
          <Select
            version="2"
            {...data}
            options={preprocessingTemplates}
            placeholder=""
            sort={false}
            loading={preprocessingConfigsLoading}
          />
        );
      },
    },
  ];

  return { uploadFile, hasOnlyDefaultProcessor, preprocessingConfigsLoading };
};
